
import { defineComponent, nextTick } from 'vue';
import { AudioPlayer, PlaybackEventType } from '../player';
import { Track, ItemStub, LibraryManager } from '../library';
import { QueueManager, PlayQueueItem, PlayQueue } from '.';
import { ITEM_STUB_MIME_TYPE } from '../common/constants';
import { RowItem, Column, ColumnPicker } from '../common/table';
import { PlayColumn, savedQueueColumns } from './columns';
import { Settings } from '../common/settings';

function rowItemsFromQueue(queue: PlayQueue): Array<RowItem<PlayQueueItem>> {
    const rowItems = [];
    for (const track of queue) {
        const rowItem = {
            id: track.queueItemId,
            selected: false,
            data: track,
            dragCount: 0,
        };
        rowItems.push(rowItem);
    }
    return rowItems;
}

export default defineComponent({
    components: { ColumnPicker },
    props: {
        player: {
            type: AudioPlayer,
            required: true,
        },
        libraryManager: {
            type: LibraryManager,
            required: true,
        },
        queueManager: {
            type: QueueManager,
            required: true,
        },
        settings: {
            type: Settings,
            required: true,
        },
    },
    data() {
        const activeQueue = this.queueManager.getActiveQueue();
        const playingQueue = this.player.getQueue();
        return {
            playQueues: this.queueManager.getQueues(),
            activeQueue,
            playingQueue,
            columns: savedQueueColumns(this.settings),
            pickingColumns: false,
            selectedItems: [] as Array<RowItem<PlayQueueItem>>,
            draggingItems: [] as Array<RowItem<PlayQueueItem>>,
            focusRowIndex: -1,
            focusColumnIndex: 0,
            dragOver: 0,
            nowPlayingIndex: -1,
            childDragOver: 0,
        };
    },
    watch: {
        columns(newColumns: Array<Column<PlayColumn, PlayQueueItem>>) {
            this.settings.set(
                'playlistColumns',
                newColumns.filter((x) => x.visible).map((x) => x.def.field)
            );
        },
    },
    created() {
        this.player.setQueue(this.activeQueue);
        this.player.playbackEvent.on((evt) => {
            if (evt.type === PlaybackEventType.Play) {
                this.nowPlayingIndex = evt.queueIndex;
            } else if (evt.type === PlaybackEventType.End) {
                this.nowPlayingIndex = -1;
            }
        });
        this.queueManager.onSwitchActive.on((evt) => {
            this.activeQueue = evt.newQueue;
        });
        this.queueManager.onSwitchPlaying.on((evt) => {
            this.playingQueue = evt.newQueue;
        });
    },
    computed: {
        visibleColumns(): Array<Column<PlayColumn, PlayQueueItem>> {
            return this.columns.filter((item) => item.visible);
        },
        queueItems(): Array<RowItem<PlayQueueItem>> {
            return rowItemsFromQueue(this.activeQueue);
        },
    },
    methods: {
        testIdFor(item: RowItem<PlayQueueItem>): string | undefined {
            if (this.nowPlayingIndex === this.queueItems.indexOf(item)) {
                return 'play-queue-active-track';
            }
        },
        isPlaying(queue: PlayQueue) {
            return queue.id === this.player.getQueue().id;
        },
        isRowFocused(rowIndex: number) {
            return (
                (this.focusRowIndex < 0 && rowIndex === 0) ||
                rowIndex === this.focusRowIndex
            );
        },
        isColumnFocused(columnIndex: number) {
            return (
                (this.focusColumnIndex < 0 && columnIndex === 0) ||
                columnIndex === this.focusColumnIndex
            );
        },
        isCellFocused(rowIndex: number, columnIndex: number) {
            return (
                this.isRowFocused(rowIndex) && this.isColumnFocused(columnIndex)
            );
        },
        itemDragStart(evt: DragEvent) {
            if (this.selectedItems.length > 0) {
                const textValues = this.selectedItems
                    .map((item) => `track: ${item.data.track.name}`)
                    .join('\n');
                evt.dataTransfer?.setData('text/plain', textValues);
                const jsonValues = this.selectedItems.map((item) => {
                    return {
                        type: item.data.track.type,
                        name: item.data.track.name,
                        id: item.data.track.id,
                    };
                });
                evt.dataTransfer?.setData(
                    ITEM_STUB_MIME_TYPE,
                    JSON.stringify(jsonValues)
                );
                this.draggingItems = [...this.selectedItems];
                const dragEl = this.$el.querySelector('.playlist__drag-info');
                evt.dataTransfer?.setDragImage(dragEl, 10, dragEl.width / 2);
            } else {
                evt.preventDefault();
            }
        },
        dragEnd() {
            this.draggingItems = [];
        },
        addTracks(
            items: Array<Track>,
            options: {
                playNow: boolean;
            }
        ) {
            const nextIndex = this.activeQueue.size();
            this.activeQueue.extend(items);
            nextTick(() => {
                this.ensureVisible(Math.max(0, this.activeQueue.size() - 2));
            });
            this.player.generateShuffleOrder();
            if (options.playNow) {
                this.playTrack(nextIndex);
            }
        },
        selectItem(item: RowItem<PlayQueueItem>, rowIndex: number) {
            for (const selectedItem of this.selectedItems) {
                selectedItem.selected = false;
            }
            item.selected = true;
            this.selectedItems = [item];
            if (this.focusRowIndex !== rowIndex) {
                this.focusItem(rowIndex);
            }
        },
        toggleSelectItem(item: RowItem<PlayQueueItem>) {
            if (item.selected) {
                item.selected = false;
                this.selectedItems = this.selectedItems.filter(
                    (i) => i.id !== item.id
                );
            } else {
                item.selected = true;
                this.selectedItems.push(item);
            }
            this.$forceUpdate();
        },
        clearSelection() {
            for (const selectedItem of this.selectedItems) {
                selectedItem.selected = false;
            }
            this.selectedItems = [];
            this.focusRowIndex = -1;
            this.focusColumnIndex = -1;
        },
        extendSelectItem(
            selectTargetItem: RowItem<PlayQueueItem>,
            targetItemIndex: number
        ) {
            if (this.selectedItems.length === 0) {
                this.selectItem(selectTargetItem, targetItemIndex);
            } else {
                let firstSelectedIndex = -1;
                for (let i = 0; i < this.queueItems.length; i++) {
                    let currentItem = this.queueItems[i];
                    if (firstSelectedIndex === -1 && currentItem.selected) {
                        firstSelectedIndex = i;
                    }
                }
                this.clearSelection();
                let startIdx = Math.min(firstSelectedIndex, targetItemIndex);
                let endIdx = Math.max(firstSelectedIndex, targetItemIndex) + 1;
                let newSelection = this.queueItems.slice(startIdx, endIdx);

                for (const item of newSelection) {
                    item.selected = true;
                }
                this.selectedItems = newSelection;
            }
        },
        playTrack(index: number) {
            if (this.activeQueue.name !== this.player.playQueue.name) {
                this.player.setQueue(this.activeQueue);
                this.playingQueue = this.activeQueue;
            }
            this.player.play(index);
        },
        focusItem(rowIndex: number, columnIndex?: number) {
            this.focusRowIndex = rowIndex;
            this.focusColumnIndex =
                columnIndex !== undefined ? columnIndex : this.focusColumnIndex;
            const focusedColumnIndex = Math.max(0, this.focusColumnIndex);
            this.$el
                .querySelector(
                    `tbody tr:nth-child(${rowIndex + 1}) td:nth-child(${
                        focusedColumnIndex + 1
                    })`
                )
                .focus({ preventScroll: true });
            this.ensureVisible(rowIndex);
        },
        focusFirst() {
            this.focusItem(0);
        },
        focusLast() {
            this.focusItem(this.queueItems.length - 1);
        },
        focusPrevious() {
            const prevItem = Math.max(0, this.focusRowIndex - 1);
            this.focusItem(prevItem);
        },
        focusNext() {
            const nextItem = Math.min(
                this.queueItems.length - 1,
                this.focusRowIndex + 1
            );
            this.focusItem(nextItem);
        },
        focusFirstColumn() {
            this.focusItem(this.focusRowIndex, 0);
        },
        focusLastColumn() {
            this.focusItem(this.focusRowIndex, this.visibleColumns.length - 1);
        },
        focusPreviousColumn() {
            const prevColumn = Math.max(0, this.focusColumnIndex - 1);
            this.focusItem(this.focusRowIndex, prevColumn);
        },
        focusNextColumn() {
            const nextColumn = Math.min(
                this.visibleColumns.length - 1,
                this.focusColumnIndex + 1
            );
            this.focusItem(this.focusRowIndex, nextColumn);
        },
        toggleSelectFocused() {
            this.toggleSelectItem(this.queueItems[this.focusRowIndex]);
        },
        removeItems(items: Array<RowItem<PlayQueueItem>>) {
            this.activeQueue.remove(items.map((x) => x.data));
            this.player.generateShuffleOrder();
        },
        removeSelectedItems() {
            this.removeItems(this.selectedItems);
            this.selectedItems = [];
        },
        playFocusedItem() {
            this.playTrack(this.focusRowIndex);
        },
        itemDragEnter(item: RowItem<PlayQueueItem>, evt: DragEvent) {
            this.childDragOver += 1;
            if (evt.dataTransfer?.getData(ITEM_STUB_MIME_TYPE)) {
                item.dragCount += 1;
                evt.preventDefault();
            }
        },
        itemDragLeave(item: RowItem<PlayQueueItem>, _evt: DragEvent) {
            this.childDragOver -= 1;
            item.dragCount -= 1;
        },
        listDragEnter(evt: DragEvent) {
            if (evt.dataTransfer?.getData(ITEM_STUB_MIME_TYPE)) {
                this.dragOver += 1;
                evt.preventDefault();
            }
        },
        listDragLeave(_evt: DragEvent) {
            this.dragOver -= 1;
        },
        async listDrop(evt: DragEvent) {
            const transferData = evt.dataTransfer?.getData(ITEM_STUB_MIME_TYPE);
            this.dragOver = 0;
            this.childDragOver = 0;
            if (transferData) {
                evt.preventDefault();
                const itemStubs = JSON.parse(transferData) as Array<ItemStub>;
                const tracksByItem = await Promise.all(
                    itemStubs.map((item) =>
                        this.libraryManager.getChildTracks(item)
                    )
                );
                let tracks: Array<Track> = [];
                for (const itemTrackList of tracksByItem) {
                    tracks = tracks.concat(itemTrackList);
                }
                this.activeQueue.extend(tracks);
            }
            if (this.draggingItems) {
                this.removeItems(this.draggingItems);
                this.draggingItems = [];
            }
        },
        async itemDrop(
            item: RowItem<PlayQueueItem>,
            index: number,
            evt: DragEvent
        ) {
            const transferData = evt.dataTransfer?.getData(ITEM_STUB_MIME_TYPE);
            item.dragCount = 0;
            this.dragOver = 0;
            this.childDragOver = 0;
            if (transferData) {
                evt.preventDefault();
                const itemStubs = JSON.parse(transferData) as Array<ItemStub>;
                const tracksByItem = await Promise.all(
                    itemStubs.map((item) =>
                        this.libraryManager.getChildTracks(item)
                    )
                );
                let tracks: Array<Track> = [];
                for (const itemTrackList of tracksByItem) {
                    tracks = tracks.concat(itemTrackList);
                }
                this.activeQueue.insert(index, tracks);
            }
            if (this.draggingItems) {
                this.removeItems(this.draggingItems);
                this.draggingItems = [];
            }
        },
        ensureVisible(index: number) {
            const trackRowRect = this.$el
                .querySelector(`tbody tr:nth-child(${index + 1})`)
                .getBoundingClientRect();
            const container = this.$el.parentElement;
            const containerRect = container.getBoundingClientRect();
            const elPosInContainer = trackRowRect.top - containerRect.top;

            if (!trackRowRect) {
                console.warn(`Tried to scroll to nonexistent index ${index}`);
                return;
            }

            if (elPosInContainer < 3 * trackRowRect.height) {
                container.scrollBy(
                    0,
                    elPosInContainer - 3 * trackRowRect.height
                );
            } else if (
                elPosInContainer + 3 * trackRowRect.height >
                containerRect.height
            ) {
                container.scrollBy(
                    0,
                    elPosInContainer +
                        3 * trackRowRect.height -
                        containerRect.height
                );
            }
        },
    },
});
